import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScrollLock from '../../../../hooks/useScrollLock';
import { GoClockFill } from 'react-icons/go';
import { BsFillCalendarDateFill, BsPersonWalking } from 'react-icons/bs';
import { FaMusic } from 'react-icons/fa';
import { HiMiniUsers } from 'react-icons/hi2';
import { PiUsersFourFill } from 'react-icons/pi';
import { MdNotes, MdCheckCircle, MdContentCopy, MdCheck, MdShare, MdReceipt } from 'react-icons/md';
import { RiUser5Fill } from 'react-icons/ri';
import { IoClose } from 'react-icons/io5';
import DownloadPDF from './DownloadPDF';

interface Session {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    notes: string | string[];
    members: string[];
    xp: number;
    music?: string;
    customMusicURL?: string;
    tasks?: { task: string; isCompleted: boolean }[];
}

interface SessionDetailsModalProps {
    open: boolean;
    onClose: () => void;
    session: Session | null;
}

// Helper function to format duration
const formatDuration = (durationInMinutes: number): string => {
    const mins = Math.floor(durationInMinutes);
    const secs = Math.round((durationInMinutes - mins) * 60);

    if (mins === 0 && secs === 0) return '0s';
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
};

// Helper function to format music type
const formatMusicType = (music: string): string => {
    const musicMap: Record<string, string> = {
        silence: 'Silence',
        noise: 'White Noise',
        nature: 'Nature Sounds',
        binaural: 'Binaural Beats',
        custom: 'Custom Music',
    };
    return musicMap[music.toLowerCase()] || music;
};

const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({
    open,
    onClose,
    session,
}) => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [notesCopied, setNotesCopied] = useState(false);

    // Prevent body scroll when modal is open
    useScrollLock(open);

    // Copy to clipboard function
    const handleCopyURL = async () => {
        if (session?.customMusicURL) {
            try {
                await navigator.clipboard.writeText(session.customMusicURL);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (error) {
                console.error('Failed to copy URL:', error);
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = session.customMusicURL;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    // Share notes function
    const handleShareNotes = async () => {
        if (!session?.notes) return;

        const shareData = {
            title: `Session Notes: ${session.goal}`,
            text: Array.isArray(session.notes) ? session.notes.join('\n') : session.notes,
        };

        // Try Web Share API first (mobile-friendly)
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (error: any) {
                // User cancelled or error occurred, fallback to copy
                if (error.name !== 'AbortError') {
                    console.error('Error sharing:', error);
                }
            }
        }

        // Fallback: Copy to clipboard
        try {
            const notesText = Array.isArray(session.notes) ? session.notes.join('\n') : session.notes;
            await navigator.clipboard.writeText(notesText);
            setNotesCopied(true);
            setTimeout(() => setNotesCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy notes:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = Array.isArray(session.notes) ? session.notes.join('\n') : session.notes;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setNotesCopied(true);
            setTimeout(() => setNotesCopied(false), 2000);
        }
    };

    const hasTasks = !!(session?.tasks && session.tasks.length > 0);

    const handleGenerateInvoice = () => {
        if (!session || !hasTasks) return;
        onClose();
        const invDate = new Date(session.completedAt).toISOString().split('T')[0];
        navigate('/invoice', {
            state: {
                session: {
                    id: session.id,
                    type: session.type,
                    goal: session.goal,
                    duration: session.duration,
                    completedAt: session.completedAt,
                    notes: session.notes,
                    members: session.members,
                    music: session.music,
                    tasks: session.tasks,
                },
                clientName: '',
                clientAddress: '',
                currency: 'USD',
                hourlyCharge: '',
                invoiceNumber: '',
                invoiceDate: invDate,
                senderName: '',
                invoiceSessionGoal: session.goal,
                invoiceTasks: session.tasks ? session.tasks.map((t) => t.task) : [],
                invoiceTotalMinutes: session.duration,
            },
        });
    };

    if (!open || !session) return null;

    const completedDate = new Date(session.completedAt);
    const formattedDate = completedDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    const formattedTime = completedDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-3xl w-full max-h-[90vh] overflow-hidden border border-gray-200/60 shadow-lg flex flex-col">
                {/* Modal Header */}
                <div className="flex justify-between items-start mb-6 flex-shrink-0">
                    <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 bg-orange-50 text-orange-600 border border-orange-200/50">
                                {session.type === 'solo' ? (
                                    <>
                                        <BsPersonWalking className="text-sm" />
                                        <span>Solo Session</span>
                                    </>
                                ) : (
                                    <>
                                        <PiUsersFourFill className="text-sm" />
                                        <span>Team Session</span>
                                    </>
                                )}
                            </div>
                            {session.completedAt && (
                                <div className="flex items-center gap-1.5 text-orange-600 text-sm">
                                    <MdCheckCircle className="text-base" />
                                    <span>Completed</span>
                                </div>
                            )}
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-medium text-gray-900">
                            {session.goal}
                        </h2>
                        <p className="text-sm text-gray-600 font-light">
                            Completed on {formattedDate} at {formattedTime}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                        <button
                            onClick={handleGenerateInvoice}
                            disabled={!hasTasks}
                            className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 bg-orange-50 text-orange-600 border border-orange-200/50 hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-50"
                            title={hasTasks ? 'Generate invoice' : 'Add at least one task to this session to generate an invoice'}
                        >
                            <MdReceipt className="text-base" />
                            <span>Generate invoice</span>
                        </button>
                        <DownloadPDF session={session} />
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
                        >
                            <IoClose />
                        </button>
                    </div>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-y-auto space-y-6 pr-2 min-h-0">
                    {/* Session Stats - Calm Metrics Only */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div className="bg-white border border-gray-200/60 rounded-xl p-4 text-center">
                            <div className="text-2xl font-medium text-gray-900 mb-1">
                                {formatDuration(session.duration)}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center justify-center gap-1 font-light">
                                <GoClockFill className="text-orange-500/70" />
                                Duration
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200/60 rounded-xl p-4 text-center">
                            <div className="text-2xl font-medium text-gray-900 mb-1">
                                {session.type === 'team' ? session.members.length : 1}
                            </div>
                            <div className="text-xs text-gray-600 flex items-center justify-center gap-1 font-light">
                                {session.type === 'team' ? (
                                    <HiMiniUsers className="text-orange-500/70" />
                                ) : (
                                    <RiUser5Fill className="text-orange-500/70" />
                                )}
                                Participants
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200/60 rounded-xl p-4 text-center sm:col-span-1 col-span-2">
                            <div className="text-2xl font-medium text-gray-900 mb-1 flex justify-center">
                                {session.type === 'team' ? (
                                    <PiUsersFourFill className="text-orange-500/70" />
                                ) : (
                                    <BsPersonWalking className="text-orange-500/70" />
                                )}
                            </div>
                            <div className="text-xs text-gray-600 mt-2 font-light">Mode</div>
                        </div>
                    </div>

                    {/* Music Mode */}
                    {session.music && (
                        <div className="bg-white border border-gray-200/60 rounded-xl p-5">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <FaMusic className="text-lg text-orange-500/70" />
                                    <h3 className="text-base font-medium text-gray-900">Music Mode</h3>
                                </div>
                                {session.music === 'custom' && session.customMusicURL && (
                                    <div className="flex items-center gap-2 bg-orange-50/30 border border-orange-200/40 rounded-lg px-3 py-1.5">
                                        <a
                                            href={session.customMusicURL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-orange-600 hover:text-orange-700 text-xs sm:text-sm truncate max-w-[200px] sm:max-w-[300px] font-normal"
                                            title={session.customMusicURL}
                                        >
                                            {session.customMusicURL}
                                        </a>
                                        <button
                                            onClick={handleCopyURL}
                                            className="flex-shrink-0 p-1.5 hover:bg-orange-100/50 text-orange-600 rounded transition-colors"
                                            title="Copy URL"
                                        >
                                            {copied ? (
                                                <MdCheck className="text-sm" />
                                            ) : (
                                                <MdContentCopy className="text-sm" />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <p className="text-sm text-gray-600 font-light">
                                {formatMusicType(session.music)}
                            </p>
                        </div>
                    )}

                    {/* Session Notes */}
                    {session.notes && (
                        <div className="bg-white border border-gray-200/60 rounded-xl p-5">
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <MdNotes className="text-lg text-orange-500/70" />
                                    <h3 className="text-base font-medium text-gray-900">Session Notes</h3>
                                </div>
                                <button
                                    onClick={handleShareNotes}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg text-xs font-medium transition-colors"
                                    title="Share or copy notes"
                                >
                                    {notesCopied ? (
                                        <>
                                            <MdCheck className="text-sm" />
                                            <span>Copied!</span>
                                        </>
                                    ) : (
                                        <>
                                            <MdShare className="text-sm" />
                                            <span>Share</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap font-light">
                                {Array.isArray(session.notes) ? session.notes.join('\n') : session.notes}
                            </p>
                        </div>
                    )}

                    {/* Tasks Section */}
                    {session.tasks && session.tasks.length > 0 && (
                        <div className="bg-white border border-gray-200/60 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <MdCheckCircle className="text-lg text-orange-500/70" />
                                <h3 className="text-base font-medium text-gray-900">Tasks</h3>
                                <span className="text-sm text-gray-500 font-light">
                                    ({session.tasks.filter(t => t.isCompleted).length}/{session.tasks.length})
                                </span>
                            </div>
                            <div className="space-y-2">
                                {session.tasks.map((task, index) => (
                                    <div key={index} className="flex items-start gap-3 p-2 hover:bg-orange-50/50 rounded-lg transition-colors">
                                        <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0
                      ${task.isCompleted ? 'bg-orange-100 border-orange-200 text-orange-600' : 'border-gray-300'}`}
                                        >
                                            {task.isCompleted && <MdCheck className="text-sm" />}
                                        </div>
                                        <span className={`text-sm ${task.isCompleted ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                                            {task.task}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Team Members (if team session) */}
                    {session.type === 'team' && session.members.length > 0 && (
                        <div className="bg-white border border-gray-200/60 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <HiMiniUsers className="text-lg text-orange-500/70" />
                                <h3 className="text-base font-medium text-gray-900">Team Members</h3>
                                <span className="text-sm text-gray-500 font-light">({session.members.length})</span>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {session.members.map((member, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-2 bg-orange-50/30 border border-orange-200/40 rounded-lg px-3 py-2"
                                    >
                                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-medium text-xs">
                                            {member.charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-sm text-gray-700 font-normal">{member}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Session Timeline */}
                    <div className="bg-white border border-gray-200/60 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <BsFillCalendarDateFill className="text-lg text-orange-500/70" />
                            <h3 className="text-base font-medium text-gray-900">Session Timeline</h3>
                        </div>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">Session Started</div>
                                    <div className="text-xs text-gray-600 font-light">
                                        {formattedDate} at {formattedTime}
                                    </div>
                                </div>
                            </div>
                            {session.completedAt && (
                                <div className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">Session Completed</div>
                                        <div className="text-xs text-gray-600 font-light">
                                            {formattedDate} at {formattedTime}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="mt-6 pt-6 border-t border-gray-200/40 flex justify-end items-center flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div >
    );
};

export default SessionDetailsModal;
